import React, { useState, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { createWidget, actionGroup } from 'actions/editor';
import { fetchLiveElements, fetchLiveElement, deleteLiveElement } from 'actions/live_elements';
import {
  LIVE_ELEMENT, LIVE_ELEMENT_CONTAINER, MENU, SECTION
} from 'constants/widgetTypes';
import StoryboardContext from 'components/Storyboard/context';

import _ from 'lodash';
import cx from 'classnames';
import shortid from 'shortid';
import { IKImage } from 'imagekitio-react';
import { toast } from "react-toastify";
import { ReactComponent as SortByLogo } from 'assets/images/sort.svg';
import { getStoryboardMixpanelEventPayload } from 'services/mixpanelUtil';
import { MIXPANEL_BRANDKIT_CREATED, MIXPANEL_WIDGET_ADDED, MIXPANEL_WIDGET_USED } from 'constants/mixpanelEvents';
import { createMixpanelEvent } from 'actions/mixpanel';

import {
  Tab,
  Button,
  Modal,
  Row,
  Col,
  Image
} from 'react-bootstrap';

import Tooltip from 'components/Tooltip';
import { ShortLoader } from 'components/ApplicationLoader';
import Portal from 'components/Portal';
import { SuccessToast } from "components/Toast";


import { PaletteContext } from '.';
import { getBoundingRectFromLayout, getPositionForNewWidget } from 'components/Editor/helper/layoutManager';
import InfiniteScroll from "components/InfiniteScroll";

import styles from './palette.module.scss';

const LiveElementsPalette = ({ editorSession, defaultParent, active }) => {
  const dispatch = useDispatch()

  const [deleteElement, setDeleteElement] = useState(null);
  const [state, setState] = useState({
    sort: 'last_published',
    order: true
  })

  const liveElements = useSelector((state) => _.get(state, 'liveElements.items', []));
  const loading = useSelector(state => _.get(state, 'liveElements.loading', false));
  const pagination = useSelector(state => _.get(state, 'liveElements.pagination'));
  const hasMoreItems = _.get(pagination, 'currentPage') < _.get(pagination, 'totalPages') - 1;
  const deleting = useSelector(state => _.get(state, 'liveElements.delete.deleting', false));
  const deleted = useSelector(state => _.get(state, 'liveElements.delete.deleted', false));

  const handleFetchLiveElements = (page) => {
    var request = {
      published: true,
      sort: state.sort,
      order: state.order ? 'DESC' : 'ASC'
    }

    dispatch(fetchLiveElements(request, page, 20));
  }

  const loadMoreLiveElements = () => {
    const nextPage = pagination.currentPage + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }

    handleFetchLiveElements(nextPage);
  }

  useEffect(() => {
    if (active) {
      handleFetchLiveElements(0);
    }
  }, [active]);

  useEffect(() => {
    if (deleted && deleteElement) {
      setDeleteElement(null);

      toast(
        <SuccessToast
          heading={`Success`}
          description={`Live element has been deleted successfully`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );

    }
  }, [deleted])

  useEffect(() => {
    handleFetchLiveElements(0);
  }, [state.order])

  const handleDelete = () => {
    if (!deleteElement) {
      return;
    }

    dispatch(deleteLiveElement(deleteElement));
  }

  const toggleOrderBy = () => {
    setState(state => {
      return {
        ...state,
        order: !state.order
      }
    })
  }

  return (
    <Tab.Pane eventKey={'LIVE_ELEMENT'} className={cx(styles.tabPane, styles.liveElementsPalette)}>
      <div className={styles.liveElementHeader}>
        <h2 className={styles.header}>Your live elements</h2>
        <div className={styles.liveElementSort}>
          <label className={styles.sortbyLabel}>Sort By:</label>
          <div className={cx(styles.sortArrows)}
            onClick={toggleOrderBy}>
            <img alt="sortIcon" src='/images/sortIcon.svg' className={cx({ [styles.up]: state.order })}/>
          </div>
          <label className={styles.fieldLabel}>Created</label>
        </div>
      </div>

      {loading && liveElements.length === 0 &&
        <ShortLoader className={styles.loader} title={'Loading live elements...'} />
      }

      {liveElements.length > 0 &&
        <InfiniteScroll
          className={cx(styles.scrollable)}
          loading={loading}
          hasMore={hasMoreItems}
          loadMore={() => {
            loadMoreLiveElements();
          }}
        >
          {liveElements.map((liveElement, index) => {
            return (
              <LiveElementsCard
                key={liveElement.id}
                liveElement={liveElement}
                editorSession={editorSession}
                defaultParent={defaultParent}
                onDelete={setDeleteElement} />
            )
          })}
        </InfiniteScroll>
      }

      {!loading && liveElements.length === 0 &&
        <LiveElementEmptyMessage />
      }

      <Portal>
        <DeleteLiveElement
          show={deleteElement !== null}
          deleting={deleting}
          onDelete={handleDelete}
          onCancel={() => setDeleteElement(null)} />
      </Portal>
    </Tab.Pane>
  )
}

const LiveElementsCard = ({ liveElement, editorSession, defaultParent, onDelete }) => {
  const storyboardContext = React.useContext(StoryboardContext);
  const dispatch = useDispatch();
  const paletteContext = React.useContext(PaletteContext);

  const creating = React.useRef(false);

  const loading = useSelector(state => _.get(state, `liveElements.liveElement.${liveElement.id}.loading`, false));
  const element = useSelector(state => _.get(state, `liveElements.liveElement.${liveElement.id}.item`, {}));
  const workspace = useSelector(state => _.get(state, `workspaces.workspace.item.id`));

  const parentWidget = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || []
    return widgets.find(widget => {
      return widget.uid === defaultParent
    });
  });

  const parentLayout = _.get(parentWidget, 'properties.desktop.layout');
  const presentwidgets = useSelector(state => _.get(state, `${editorSession}.present.widgets`, {}));

  useEffect(() => {
    if (element && element.id === liveElement.id && creating.current) {
      creating.current = false;
      let stretchcheck= false;

      var uid = `widget_${shortid.generate()}`
      var widgets = _.get(element, 'content.widgets');

      var liveElementContainer = (widgets || []).find(item => {
        return item.type === LIVE_ELEMENT_CONTAINER
      });

      const rect = getBoundingRectFromLayout(_.get(liveElementContainer, 'properties.desktop.layout'));
      var menuStretch = presentwidgets.map((item) => {
        if (item.type === LIVE_ELEMENT && item.children) {
          item.children.map((widget) => {
            if (widget.type === MENU || widget.type === SECTION) {
              stretchcheck = true
            }
          });
        }
      });
      const position = getPositionForNewWidget(editorSession, parentLayout, {
        width: rect?.width,
        height: rect?.height
    }, stretchcheck);

      var liveElementWidgetEntity = {
        uid: uid,
        type: LIVE_ELEMENT,
        name: _.get(liveElement, 'name'),
        parent: parentWidget.uid,
        content: {
          id: element.id,
          createdBy: element.createdBy,
        },
        properties: {
          "desktop": {
            "visible": true,
            "layout": {
              "transform": {
                "translate": `${position.x}px, ${position.y}px`,
              },
              width: _.get(liveElementContainer, 'properties.desktop.layout.width'),
              height: _.get(liveElementContainer, 'properties.desktop.layout.height'),
            }
          },
          "mobile": {
            "visible": true,
            "layout": {
              "transform": {
                "translate": stretchcheck ? "0px, 20px" : "20px, 20px"
                },
              width: _.get(liveElementContainer, 'properties.mobile.layout.width'),
              height: _.get(liveElementContainer, 'properties.mobile.layout.height'),
            }
          }
        },
        themes: _.get(element, 'themes'),
        children: widgets
      }

      dispatch(createWidget(editorSession, [liveElementWidgetEntity], parentWidget.uid));

      paletteContext.close();
    }
  }, [element]);

  const handleClick = () => {
    var mixpanelPayload = getStoryboardMixpanelEventPayload(storyboardContext.id, storyboardContext.name, storyboardContext.type);
    mixpanelPayload = {
      ...mixpanelPayload,
      widget_type: "Live Element",
      brandkit_widget: false,
    }
    dispatch(createMixpanelEvent(MIXPANEL_WIDGET_USED, mixpanelPayload))
    creating.current = true;
    dispatch(fetchLiveElement(liveElement.id));
  }

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(liveElement.id);
  }

  const handleEdit = (e) => {
    e.stopPropagation();
    const url = `${process.env.REACT_APP_UI_URL}/app/${workspace}/liveElement/${liveElement.id}`;
    window.open(url, '_blank');
  }

  const handleClone = (e) => {
    e.stopPropagation();
    const url = `${process.env.REACT_APP_UI_URL}/app/${workspace}/liveElement/${liveElement.id}/clone`;
    window.open(url, '_blank');
    paletteContext.close();
  }

  return (
    <div key={liveElement.id} className={cx(styles.card, {
      [styles.adding]: loading
    })} onClick={handleClick}>
      <div className={styles.icon}>
        {liveElement.icon &&
          <IKImage
            path={_.get(liveElement, 'icon.url')}
            transformation={[{
              "width": "600"
            }]}
          />
        }

        {!liveElement.icon &&
          <div className={styles.empty}>Generating preview...</div>
        }
      </div>
      <div className={styles.details}>
        <div className={styles.title}>
          <div>{liveElement.name}</div>
        </div>
        <div className={styles.toolbar}>
          <Tooltip placement='top' content='Clone' className={styles.tooltip}>
            <Button className={styles.actionGroup} variant="info" onClick={handleClone}>
              <i className={cx("cleverstory-clone")} />
            </Button>
          </Tooltip>

          {liveElement.editable &&
            <>
              <Tooltip placement="top" content="Edit">
                <Button className={styles.actionGroup} variant="info" onClick={handleEdit}>
                  <i className={cx("cleverstory-edit")} />
                </Button>
              </Tooltip>
              <Tooltip placement='top' content='Delete'>
                <Button className={styles.actionGroup} variant="info" onClick={handleDelete}>
                  <i className={cx("cleverstory-trash")} />
                </Button>
              </Tooltip>
            </>
          }
        </div>
      </div>

      {loading &&
        <div className={styles.loader}>
          <div className={styles.inner}>Adding element...</div>
        </div>
      }
    </div>
  )
}

const DeleteLiveElement = ({ show, deleting, onDelete, onCancel }) => {
  return (
    <Modal backdrop='static'
      className={styles.deleteConfirmModal}
      contentClassName={styles.deleteConfirmContent}
      dialogClassName={styles.deleteConfirmDialog}
      backdropClassName={styles.backdropWrapper}
      show={show}
      centered size="lg"
    >
      <Modal.Body className={styles.deleteConfirmBody}>
        <Image src={'/images/deleteConfirm.png'} className={styles.deleteConfirmImage} />
        <Row className={styles.messageRow}>
          <Col className={styles.messageContainer} xs={{ span: 6, offset: 6 }}>
            <h2 className={styles.messageTitle}>Are you sure?</h2>
            <span className={styles.messageContent}>
              This action of yours will delete the current live element across the workspace.
            </span>
            <span className={styles.messageContent}>
              Sure you want to continue?
            </span>
            <footer className={styles.deleteConfirmButtons}>
              <Button variant="secondary" onClick={onCancel}>Cancel</Button>
              <Button variant="primary" disabled={deleting} onClick={onDelete}>{deleting ? 'Deleting...' : 'Delete'}</Button>
            </footer>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  )
}

const LiveElementEmptyMessage = () => {
  return (
    <div className={styles.liveElementEmptyScreen}>
      <div className={styles.details}>
        <h5>What is a Live Element?</h5>
        <p>It is a custom widget that can be built from scratch and used across storyboards.  Any change made in a live element will reflect across all the instances in which it is used.</p>
      </div>
      <div>
        <img src={'/images/liveElementsEmptyScreen.png'} alt="" />
      </div>
      <div className={styles.listContainer}>
        <h5>To Create a Live Element:</h5>
        <ol>
          <li>Select one or more elements in the editor.</li>
          <li>Click the 'Make Live Element' option.</li>
          <li>You can find the saved Live Elements in the palette as a draggable widget.</li>
        </ol>
      </div>
    </div>
  )
}

export { LiveElementsPalette };